import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import {
  Card,
  EmptyState,
  FILL_BODY,
  FILL_CARD,
  FILL_PAGE,
  PageHeader,
  Pagination,
  ROW_SCROLL,
  STICKY_THEAD,
  usePageSize,
} from "../components/account-ui.tsx";
import { useAppDm } from "../components/legacy/app-state.tsx";
import { CampaignOrderSheet } from "../components/legacy/FinaliseCampaign.tsx";
import { Modal } from "../components/legacy/ui.tsx";
import {
  CampaignStatusBadge,
  FinalPdfBadge,
  StatusLegend,
  StatusLegendToggle,
} from "../components/status.tsx";
import {
  CampaignOutlineIcon,
  EyeballIcon,
  PDFIcon,
  ShoppingBagIcon,
  ZipDownloadIcon,
} from "../icons/index.tsx";
import type { Campaign } from "../lib/api.ts";
import {
  effectiveStatus,
  formatDate,
  formatPrices,
  formatTime,
  prefixZeros,
  startOfToday,
} from "../lib/legacy.ts";

export const Route = createFileRoute("/app/$customerId/myorders")({ component: MyOrders });

type FilterBy = "All" | "Complete" | "Past" | "Cancelled" | "Rejected";

const FILTERS: { by: FilterBy; label: string }[] = [
  { by: "Complete", label: "Active" },
  { by: "Past", label: "Past" },
  { by: "Cancelled", label: "Cancelled" },
  { by: "Rejected", label: "Rejected" },
  { by: "All", label: "All" },
];

const OUTLINE =
  "inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50";
const CHIP = "inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium";
const ICON_BTN =
  "flex h-9 w-9 items-center justify-center rounded-lg text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900";

/** The legacy "Campaign Orders" page: campaigns filtered by lifecycle, with the order sheet in a modal. */
function MyOrders() {
  const dm = useAppDm();
  const navigate = useNavigate();
  const { ui, set, customerId, adminMode, products, tiers } = dm;
  const [filter, setFilter] = useState<FilterBy>("Complete");
  const [detail, setDetail] = useState<Campaign | null>(null);
  const [perPage, setPerPage] = usePageSize();
  const listKey = JSON.stringify([filter, perPage, ui.showDeletedCampaigns]);
  const [paged, setPaged] = useState({ key: listKey, n: 0 });

  useEffect(() => {
    if (ui.appContext !== "orders") set({ appContext: "orders" });
  }, [ui.appContext, set]);

  const today = startOfToday();
  // Staff on their own workspace see every customer's orders; on a
  // customer's workspace they see that customer's, like the legacy
  // "Customer Orders" button.
  const allOrders = adminMode && customerId === dm.user.id;
  const filtered = dm.campaigns
    .filter((c) => allOrders || (c.customer_id ?? c.user_id) === customerId)
    .filter((c) => {
      if (filter === "All") return c.status !== "deleted" || ui.showDeletedCampaigns;
      if (!c.lodgement_date) return false;
      const lodgement = c.lodgement_date * 1000;
      if (filter === "Past")
        return lodgement < today && (c.status === "complete" || c.status === "posted");
      if (filter === "Complete") return lodgement > today && c.status === "complete";
      if (filter === "Cancelled") return c.status === "cancelled";
      if (filter === "Rejected") return c.status === "rejected";
      return false;
    })
    .sort((a, b) => b.created_at - a.created_at);
  const page = Math.min(
    paged.key === listKey ? paged.n : 0,
    Math.max(0, Math.ceil(filtered.length / perPage) - 1),
  );
  const shown = filtered.slice(page * perPage, (page + 1) * perPage);

  const unitCents = (c: Campaign) => {
    let chosen = 0;
    for (const t of tiers
      .filter((t) => t.product_id === c.product_id)
      .sort((a, b) => a.min_qty - b.min_qty)) {
      if (c.address_count >= t.min_qty) chosen = t.unit_cents;
    }
    return chosen;
  };
  const filterLabel = FILTERS.find((f) => f.by === filter)?.label ?? "";
  const toggleLabels = () => set({ showStatusPills: !ui.showStatusPills });

  return (
    <div className={FILL_PAGE}>
      <div className="shrink-0">
        <PageHeader
          title={`${filterLabel} campaign orders`}
          subtitle={`${filtered.length} ${filtered.length === 1 ? "campaign" : "campaigns"}`}
          action={
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                className={OUTLINE}
                onClick={() => {
                  set({ showDeletedCampaigns: false, appContext: "myCampaigns" });
                  navigate({ to: "/app/$customerId/mycampaigns", params: { customerId } });
                }}
              >
                My Campaigns
              </button>
              <button
                type="button"
                aria-pressed={ui.showDeletedCampaigns}
                className={`${OUTLINE} ${ui.showDeletedCampaigns ? "border-amber-300 bg-amber-50 text-amber-800" : ""}`}
                onClick={() => set({ showDeletedCampaigns: !ui.showDeletedCampaigns })}
              >
                {ui.showDeletedCampaigns ? "Hide Deleted" : "Show Deleted"}
              </button>
              <StatusLegendToggle
                show={ui.showLegend}
                onToggle={() => set({ showLegend: !ui.showLegend })}
              />
            </div>
          }
        />
        <StatusLegend
          show={ui.showLegend}
          adminMode={adminMode}
          todaysDate={formatDate(new Date().toISOString())}
          showLabels={ui.showStatusPills}
          onToggleLabels={toggleLabels}
        />

        <div className="mb-4 inline-flex rounded-xl bg-gray-100 p-1">
          {FILTERS.map((f) => {
            const active = filter === f.by;
            return (
              <button
                type="button"
                key={f.by}
                aria-pressed={active}
                className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
                  active ? "bg-white text-gray-900 shadow-sm" : "text-gray-600 hover:text-gray-900"
                }`}
                onClick={() => setFilter(f.by)}
              >
                {f.label}
              </button>
            );
          })}
        </div>
      </div>

      <Card className={FILL_CARD} bodyClass={FILL_BODY}>
        {filtered.length === 0 ? (
          <EmptyState
            Icon={ShoppingBagIcon}
            title={`No ${filterLabel.toLowerCase()} campaign orders`}
            message="Campaigns show here once they have a lodgement date."
          />
        ) : (
          <>
            <div className={ROW_SCROLL}>
              <table className="w-full text-left text-sm">
                <thead className={STICKY_THEAD}>
                  <tr>
                    <th className="px-5 py-3 font-semibold">#</th>
                    <th className="px-5 py-3 font-semibold">Campaign</th>
                    <th className="hidden px-5 py-3 font-semibold md:table-cell">Dates</th>
                    <th className="px-5 py-3 text-right font-semibold">Price</th>
                    <th className="px-5 py-3 text-right font-semibold">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {shown.map((c, i) => {
                    const product = products.find((p) => p.id === c.product_id);
                    const status = effectiveStatus(c);
                    const mailSpeed = c.mail_speed === "regular" ? "Regular Mail" : "Priority Mail";
                    return (
                      <tr key={c.id}>
                        <td className="px-5 py-4">
                          <div className="flex flex-col items-start gap-1.5">
                            <span className="font-mono text-xs text-gray-500">
                              {prefixZeros(filtered.length - (page * perPage + i))}
                            </span>
                            <CampaignStatusBadge
                              status={status}
                              showLabel={ui.showStatusPills}
                              onToggle={toggleLabels}
                            />
                            <FinalPdfBadge
                              status={c.final_pdf_status}
                              updatedAt={c.final_pdf_updated_at}
                            />
                          </div>
                        </td>
                        <td className="px-5 py-4">
                          <div className="flex items-start gap-3">
                            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-gray-50">
                              {c.artwork_id ? (
                                <a
                                  href={`/api/artwork/${c.artwork_id}/file`}
                                  target="_blank"
                                  rel="noreferrer"
                                  title="Open artwork"
                                >
                                  <PDFIcon />
                                </a>
                              ) : (
                                <img
                                  src="/icons/PDFIcon.svg"
                                  alt=""
                                  width="32"
                                  height="32"
                                  className="opacity-30"
                                />
                              )}
                            </div>
                            <div className="min-w-0">
                              <p className="flex items-center gap-1.5 font-medium text-gray-900">
                                <img
                                  width="10"
                                  height="10"
                                  src={`/icons/${c.mail_speed === "regular" ? "regular-mail-icon.svg" : "priority-mail-icon.svg"}`}
                                  alt={mailSpeed}
                                  title={mailSpeed}
                                />
                                <span className="truncate">{c.name}</span>
                              </p>
                              <div className="mt-1.5 flex flex-wrap gap-1">
                                <span
                                  className={`${CHIP} bg-gray-100 text-gray-700`}
                                  title="Product"
                                >
                                  {product ? product.name : "No product selected"}
                                </span>
                                <span
                                  title="Mail type"
                                  className={`${CHIP} ${
                                    c.mail_type === "addressed"
                                      ? "bg-emerald-50 text-emerald-700"
                                      : "bg-sky-50 text-sky-700"
                                  }`}
                                >
                                  {c.mail_type === "addressed" ? "Addressed" : "Unaddressed"}
                                </span>
                                {product && (
                                  <span
                                    className={`${CHIP} bg-violet-50 text-violet-700`}
                                    title="Paper size"
                                  >
                                    {product.size}
                                    <span className="ml-1 font-normal text-violet-600">
                                      {product.width_mm} x {product.height_mm}mm
                                    </span>
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="hidden whitespace-nowrap px-5 py-4 text-gray-600 md:table-cell">
                          <div className="flex flex-col gap-1">
                            <span
                              className="inline-flex items-center gap-1.5"
                              title="Lodgement date"
                            >
                              <img
                                width="14"
                                height="14"
                                src="/icons/australia-post-icon.svg"
                                alt=""
                              />
                              <span className="font-medium text-gray-900">
                                {formatDate(c.lodgement_date)}
                              </span>
                            </span>
                            <span className="text-xs text-gray-500" title="Created">
                              Created {formatDate(c.created_at)} {formatTime(c.created_at)}
                            </span>
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-5 py-4 text-right font-semibold tabular-nums text-gray-900">
                          {c.address_count > 0 && product
                            ? `$${formatPrices((c.address_count * unitCents(c)) / 100)}`
                            : "TBA"}
                        </td>
                        <td className="px-5 py-4">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              type="button"
                              className={ICON_BTN}
                              aria-label="Download campaign ZIP"
                              title="Download Campaign ZIP"
                              onClick={() => alert("Download Campaign ZIP is coming soon!")}
                            >
                              <ZipDownloadIcon width={20} height={20} />
                            </button>
                            <button
                              type="button"
                              className={ICON_BTN}
                              aria-label="Order details"
                              title="Order details"
                              onClick={() => setDetail(c)}
                            >
                              <EyeballIcon width={20} height={20} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <Pagination
              page={page}
              pageSize={perPage}
              total={filtered.length}
              onPage={(n) => setPaged({ key: listKey, n })}
              onPageSize={setPerPage}
            />
          </>
        )}
      </Card>

      <Modal
        open={Boolean(detail)}
        title="Campaign Order Details"
        onClose={() => setDetail(null)}
        icon={<CampaignOutlineIcon />}
      >
        <div className="space-y-4">
          {detail && <CampaignOrderSheet campaign={detail} showAdminZone />}
        </div>
      </Modal>
    </div>
  );
}
